import { route } from 'preact-router';
import { useState } from 'preact/hooks';
import { StarRating } from '../../components/StarRating.js';
import { Button } from '../../components/ui/Button.js';
import { PageHeader } from '../../components/ui/PageHeader.js';
import { BackToFirstPage, Pager } from '../../components/ui/Pager.js';
import { ResultState } from '../../components/ui/ResultState.js';
import { Spinner } from '../../components/ui/Spinner.js';
import { toRequestedPage } from '../../components/ui/pageParam.js';
import { pageGoneMessage } from '../../components/ui/resultWording.js';
import { useFetch } from '../../hooks/useFetch.js';
import { useSignInRedirect } from '../../hooks/useSignInRedirect.js';
import { api } from '../../services/api.js';
import { mytDate } from '../../utils/mytTime.js';

interface MyReview {
  id: string;
  rating: number;
  comment?: string;
  car?: { id: string; name: string } | null;
  created_at: string;
}

/**
 * H17: the envelope `GET /reviews` has always sent. The screen used to declare only `items` and
 * read only `items`, which is why a customer with more than 20 reviews was shown 20, presented as
 * all of them — 066's D5 defect, one screen over.
 *
 * Transcribed from `backend/src/modules/reviews/service.ts:84`.
 */
interface MyReviewsResponse {
  /** One page of reviews. **Empty does NOT mean the customer has none** — see `total`. */
  items: MyReview[];
  /** Reviews matching across ALL pages. */
  total: number;
  /** Echoed from the request. `GET /reviews` does not clamp it at all — never assume it is <= `pages`. */
  page: number;
  /** `Math.ceil(total / 20)`, so **0** for a customer with no reviews. */
  pages: number;
}

interface MyReviewsPageProps {
  path?: string;
  /**
   * H17: the page being asked for, arriving as a **string** from the address —
   * `/account/reviews?page=3`. preact-router parses the query string into route props, and
   * `AuthGuard` — the router's matched child, not this screen — forwards them with
   * `cloneElement(child, routeProps)` (`AuthGuard.tsx:37`).
   *
   * The address is the state: it survives opening a review's car and coming back, it can be
   * shared and bookmarked, and because the page number ends up in the fetch path, a retry
   * re-requests the page that failed rather than page 1.
   */
  page?: string;
}

/**
 * H17: moving pages is a navigation, not a state change.
 *
 * Page 1 is the plain address, so the customer's normal entry point never grows a redundant
 * `?page=1` and the two spellings of "the first page" do not both end up in their history.
 */
function goToPage(n: number): void {
  route(n <= 1 ? '/account/reviews' : `/account/reviews?page=${n}`);
}

export function MyReviewsPage({ page }: MyReviewsPageProps) {
  const goToSignIn = useSignInRedirect();
  const requestedPage = toRequestedPage(page);
  const reviewsRead = useFetch<MyReviewsResponse>(`/reviews?mine=1&page=${requestedPage}`);
  const { data, status } = reviewsRead;
  const loading = status === 'loading' || status === 'idle';

  return (
    // 137: bounded to exactly the space the shell leaves available (100dvh minus its 132px
    // chrome, unchanged since 134/136) and clipped, not merely floored — the outer page never
    // overflows, so the shell's bell (above this page, outside it) is never displaced by
    // scrolling. Only the region below the header scrolls.
    <div className="page-bound bg-bg px-4 pt-6 flex flex-col">
      <PageHeader title="My Reviews" fallback="/account" />
      <div
        data-testid="page-scroll-region"
        className="flex-1 overflow-y-auto overscroll-y-contain pb-6"
      >
        {loading ? (
          <div className="flex justify-center py-16">
            <Spinner size="lg" />
          </div>
        ) : status !== 'ok' ? (
          /* 064: a refused read used to render as "No reviews yet". */
          <>
            <ResultState state={reviewsRead} onRetry={reviewsRead.retry} onSignIn={goToSignIn} />
            {/* H17: a read that failed on page 3 leaves the customer with a retry and nothing
              leading back to the reviews they were just reading. Offered only past the first
              page, where there is somewhere to go back to. */}
            {requestedPage > 1 && (
              <div className="flex justify-center">
                <BackToFirstPage onFirstPage={() => goToPage(1)} />
              </div>
            )}
          </>
        ) : data && !data.items.length && data.total > 0 ? (
          /* H17: an EMPTY SLICE of a non-empty set. `page` is echoed by the platform and never
           clamped down to `pages`, so a stale bookmark or a shrunken history lands here with 45
           reviews and nothing on this page. Falling through to the copy below would tell a
           customer with a full history that they have none: 064's defect, one screen over. */
          <div className="flex flex-col items-center gap-3 py-16">
            <p className="text-center text-error">{pageGoneMessage('reviews')}</p>
            <BackToFirstPage onFirstPage={() => goToPage(1)} />
          </div>
        ) : !data?.items.length ? (
          <p className="text-center text-gray-400 py-16">No reviews yet</p>
        ) : (
          <div className="flex flex-col gap-3">
            {data.items.map((r) => (
              <ReviewCard key={r.id} review={r} />
            ))}
            {/* Rendered only on a successful read, where `pages` is something the platform just
              said. A pager painted from a remembered count beside a failed page would be the app
              asserting what it was not told. */}
            <Pager page={data.page} pages={data.pages} onPage={goToPage} />
          </div>
        )}
      </div>
    </div>
  );
}

function ReviewCard({ review }: { review: MyReview }) {
  const [editing, setEditing] = useState(false);
  const [rating, setRating] = useState(review.rating);
  const [comment, setComment] = useState(review.comment ?? '');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [current, setCurrent] = useState(review);

  async function save() {
    if (rating < 1) {
      setErr('Please select a rating');
      return;
    }
    setSaving(true);
    setErr('');
    try {
      await api.patch(`/reviews/${current.id}`, { rating, comment });
      setCurrent({ ...current, rating, comment });
      setEditing(false);
    } catch (e) {
      setErr((e as { message?: string }).message ?? 'Failed to save review');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="bg-surface-card rounded-xl p-4 flex flex-col gap-2">
      <div className="flex justify-between items-center">
        <span className="text-text font-semibold">
          {current.car ? current.car.name : <span className="text-gray-500">Car unavailable</span>}
        </span>
        <span className="text-xs text-gray-400">{mytDate(current.created_at)}</span>
      </div>

      {editing ? (
        <div data-testid="review-form" className="flex flex-col gap-2">
          <StarRating value={rating} onChange={setRating} />
          <textarea
            data-testid="review-comment"
            value={comment}
            onInput={(e) => setComment((e.target as HTMLTextAreaElement).value)}
            maxLength={2000}
            rows={3}
            className="w-full rounded-lg bg-surface-mid text-text p-3 text-sm"
          />
          {err && <p className="text-sm text-red-400">{err}</p>}
          <div className="flex gap-2">
            <Button data-testid="review-submit" loading={saving} onClick={save}>
              Save Changes
            </Button>
            <Button variant="ghost" onClick={() => setEditing(false)}>
              Cancel
            </Button>
          </div>
        </div>
      ) : (
        <>
          <StarRating value={current.rating} />
          {current.comment && <p className="text-sm text-text">{current.comment}</p>}
          <Button
            variant="ghost"
            className="self-start"
            data-testid={`review-edit-${current.id}`}
            onClick={() => {
              setRating(current.rating);
              setComment(current.comment ?? '');
              setEditing(true);
            }}
          >
            Edit
          </Button>
        </>
      )}
    </div>
  );
}
